import React, { forwardRef, useContext } from "react";
import { cn } from "../../lib/utils.js";
import {
  DESCRIPTION_LIST_LAYOUT,
  DescriptionListLayoutContext,
} from "./descriptionListLayout.js";

export interface DescriptionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The term, rendered as the `<dt>`. */
  label: React.ReactNode;
  /** Hover hint on the label, set as the `<dt>`'s `title`. */
  hint?: string;
  /** Span every column of the list. */
  fullWidth?: boolean;
  /** The value, rendered inside the `<dd>`: text, a badge, a KPIValue… */
  children: React.ReactNode;
}

/* One label/value group (div > dt + dd) inside a `DescriptionList`; the
   list's `layout` decides how the two sit. To show a figure, put a KPIValue
   WITHOUT its own `label` in `children`: this item's label is the dt. */
export const DescriptionItem = forwardRef<HTMLDivElement, DescriptionItemProps>(
  function DescriptionItem(
    { label, hint, fullWidth = false, className, children, ...rest },
    ref,
  ) {
    const classes =
      DESCRIPTION_LIST_LAYOUT[useContext(DescriptionListLayoutContext)];
    return (
      <div
        ref={ref}
        className={cn(classes.item, fullWidth && classes.fullWidth, className)}
        {...rest}
      >
        <dt title={hint} className={classes.term}>
          {label}
        </dt>
        <dd className={classes.details}>{children}</dd>
      </div>
    );
  },
);
